import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import type { VirtuosoHandle } from 'react-virtuoso';
import { getMessageShortId } from './team-inbox-helpers';

/**
 * Cross-tree bridge for "jump to the quoted message". A reply's quote block lives
 * deep inside a bubble, but the imperative Virtuoso handle + the flattened
 * displayItems list live in the list component. This context exposes a single
 * `scrollToMessage(shortId)` that resolves the target's DATA-RELATIVE index and
 * scrolls to it, plus a transient `highlightedMessageId` that a bubble reads to
 * flash itself (WhatsApp's blue-ish highlight on the jumped-to message).
 */
interface MessageScrollContextValue {
    /** Scroll to the message whose short (stanza) id matches, and flash it.
     *  Pages older history in until the target is found (WA-style) or history
     *  is exhausted. Async, but callers may fire-and-forget. */
    scrollToMessage: (shortId: string) => void | Promise<void>;
    /** _id of the message currently flashing (or null). */
    highlightedMessageId: string | null;
}

const MessageScrollContext = createContext<MessageScrollContextValue>({
    scrollToMessage: () => { /* no-op default */ },
    highlightedMessageId: null,
});

export const useMessageScroll = (): MessageScrollContextValue => useContext(MessageScrollContext);

export const HIGHLIGHT_DURATION_MS = 1500;
// Cap how many older pages we'll fetch chasing a quoted original before giving up.
const MAX_PAGES = 12;
// Per-page wait budget: how long we allow one older-page fetch to settle.
const PAGE_WAIT_MS = 4000;
const POLL_INTERVAL_MS = 80;

type DisplayItem = { type: 'header'; timestamp: number } | { type: 'message'; data: any };

export interface ScrollControllerDeps {
    /** Trigger loading the next older page (mirrors the list's startReached). */
    loadOlder: () => void;
    /** Whether more history exists to page in (paging.hasMoreInStore). */
    hasMore: boolean;
    /** Whether a message fetch is currently in flight. */
    isLoading: boolean;
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Poll `predicate` every POLL_INTERVAL_MS until it returns true or `timeoutMs`
 * elapses. Resolves true if the predicate fired, false on timeout.
 */
async function waitUntil(predicate: () => boolean, timeoutMs: number): Promise<boolean> {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
        if (predicate()) return true;
        // eslint-disable-next-line no-await-in-loop
        await wait(POLL_INTERVAL_MS);
    }
    return predicate();
}

/**
 * Build the controller value for the provider. Lives in the list component so it
 * can close over the live Virtuoso handle and the current displayItems.
 *
 * Index mapping (verified against react-virtuoso 4.18.1): `scrollToIndex` takes a
 * DATA-RELATIVE index (0..displayItems.length-1) — the same space as
 * `initialTopMostItemIndex` — NOT the firstItemIndex-offset index that
 * `itemContent` receives. Internally the size/offset tree is keyed by
 * `originalIndex` (data-relative) and the scroll handler clamps to `totalCount`
 * (= displayItems.length); firstItemIndex is added only for the item key + the
 * `index` reported to itemContent. So we pass the raw findIndex result.
 *
 * Reply-paging: the target quote may be older than the loaded window. We page
 * older history in (reusing the list's own `loadOlder`) and re-check until found
 * or exhausted. Because paging grows displayItems (and shifts firstItemIndex) via
 * a re-render, the running async loop must read the FRESHEST list — so we mirror
 * displayItems/hasMore/isLoading into refs updated every render and read those,
 * never the closed-over values. The final index is recomputed on the freshest
 * list immediately before scrollToIndex.
 */
export function useMessageScrollController(
    virtuosoRef: React.RefObject<VirtuosoHandle | null>,
    displayItems: DisplayItem[],
    deps?: ScrollControllerDeps,
): MessageScrollContextValue {
    const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
    const clearTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    // Prevents rapid clicks from stacking overlapping paging loops.
    const inProgressRef = useRef(false);

    // Refs mirrored every render so the async loop reads the latest values.
    const displayItemsRef = useRef(displayItems);
    displayItemsRef.current = displayItems;
    const hasMoreRef = useRef(!!deps?.hasMore);
    hasMoreRef.current = !!deps?.hasMore;
    const isLoadingRef = useRef(!!deps?.isLoading);
    isLoadingRef.current = !!deps?.isLoading;
    const loadOlderRef = useRef<(() => void) | undefined>(deps?.loadOlder);
    loadOlderRef.current = deps?.loadOlder;

    const flash = useCallback((targetId: string | null) => {
        setHighlightedMessageId(targetId);
        if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
        clearTimerRef.current = setTimeout(() => setHighlightedMessageId(null), HIGHLIGHT_DURATION_MS);
    }, []);

    const scrollToMessage = useCallback(async (shortId: string) => {
        if (!shortId || inProgressRef.current) return;
        inProgressRef.current = true;
        try {
            const findIdx = () => displayItemsRef.current.findIndex(
                (it) => it.type === 'message' && getMessageShortId(it.data) === shortId,
            );

            let dataIndex = findIdx();
            let pages = 0;
            // Page older history in until the target surfaces or we run out.
            while (dataIndex < 0 && hasMoreRef.current && pages < MAX_PAGES) {
                // Only trigger a fetch when idle (loadOlder itself guards on this,
                // but waiting avoids a wasted no-op call).
                if (isLoadingRef.current) {
                    // eslint-disable-next-line no-await-in-loop
                    await waitUntil(() => !isLoadingRef.current, PAGE_WAIT_MS);
                }
                const beforeLen = displayItemsRef.current.length;
                loadOlderRef.current?.();
                // Wait for the page to land (list grew AND fetch settled) or for
                // history to be exhausted.
                // eslint-disable-next-line no-await-in-loop
                await waitUntil(
                    () => (displayItemsRef.current.length !== beforeLen && !isLoadingRef.current)
                        || !hasMoreRef.current,
                    PAGE_WAIT_MS,
                );
                pages++;
                dataIndex = findIdx();
            }

            // Recompute on the FINAL (freshest) list right before scrolling.
            dataIndex = findIdx();
            if (dataIndex < 0) return; // not loaded / exhausted → quiet no-op

            virtuosoRef.current?.scrollToIndex({ index: dataIndex, align: 'center', behavior: 'smooth' });
            const targetId = (displayItemsRef.current[dataIndex] as { type: 'message'; data: any })?.data?._id ?? null;
            flash(targetId);
        } finally {
            inProgressRef.current = false;
        }
    }, [virtuosoRef, flash]);

    return { scrollToMessage, highlightedMessageId };
}

export const MessageScrollProvider: React.FC<{
    value: MessageScrollContextValue;
    children: React.ReactNode;
}> = ({ value, children }) => (
    <MessageScrollContext.Provider value={value}>{children}</MessageScrollContext.Provider>
);

export default MessageScrollContext;
